package one.devsbox.www.ui.company

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.ListBoxOutline
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.ui.widgets.CategorySplashUI
import one.devsbox.www.ui.widgets.ContentActions
import one.devsbox.www.ui.widgets.FullscreenErrorUI
import one.devsbox.www.ui.widgets.FullscreenLoadingUI
import one.devsbox.www.ui.widgets.LabelField
import one.devsbox.www.ui.widgets.SearchBox
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState

@Composable
@ComposableNavNode
fun ListingCompanyUI() {
    with(ListingCompanyCtx.get()) {
        ContentListingCompanyUI()
    }
}

@Composable
context(ctx: ListingCompanyCtx)
private fun ContentListingCompanyUI() {
    Box(modifier = Modifier.fillMaxSize()) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize()) {
                    val resultData = state.nnvl
                    if (resultData.count == 0) {
                        CategorySplashUI(
                            icon = Svg.ListBoxOutline,
                            title = Res.string.ui_company_splash_title,
                            explanation = Res.string.ui_company_splash_desc,
                            actions = ctx.vm.contentActions
                        )
                    } else {
                        SuccessUI(resultData)
                        ContentActions(
                            contentActions = ctx.vm.contentActions,
                            colorInactive = ctx.colorSecondaryContainer,
                            colorActive = ctx.colorPrimaryContainer
                        )
                    }
                }
            }
        }
    }

    CompanyActionsDialog()
    CreateCompanyDialog()
}

@Composable
context(ctx: ListingCompanyCtx)
private fun SuccessUI(data: SharedIO.Companies) {
    val filteredItems = data.items.filter {
        it.name.contains(ctx.vm.search.text, ignoreCase = true)
    }.sortedByDescending { it.id }

    Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            SearchBox(
                state = ctx.vm.search,
                hint = stringResource(Res.string.ui_company_listing_search_placeholder),
                modifier = Modifier.weight(1f),
            )
        }

        Spacer(Modifier.height(16.dp))

        val showingStart = if (filteredItems.isEmpty()) 0 else 1
        val showingEnd = filteredItems.size
        val totalCount = data.count

        Text(
            text = stringResource(
                Res.string.ui_company_listing_showing,
                showingStart,
                showingEnd,
                totalCount
            ),
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f),
            letterSpacing = 1.sp,
            modifier = Modifier.padding(bottom = 16.dp)
        )

        if (filteredItems.isEmpty()) {
            Box(
                modifier = Modifier.fillMaxWidth().weight(1f),
                contentAlignment = Alignment.Center
            ) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Icon(
                        imageVector = Svg.ListBoxOutline,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                        modifier = Modifier.size(48.dp)
                    )
                    Spacer(Modifier.height(16.dp))
                    Text(
                        text = stringResource(Res.string.ui_company_listing_no_companies_found),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                    )
                }
            }
        } else {
            // Table Header
            Row(
                modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                LabelField(text = stringResource(Res.string.ui_company_listing_header_name))
                LabelField(text = stringResource(Res.string.ui_company_listing_header_counters))
            }
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

            LazyColumn(
                modifier = Modifier.weight(1f).fillMaxWidth(),
                contentPadding = PaddingValues(bottom = 250.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                item {
                    Spacer(Modifier.height(8.dp))
                }

                items(filteredItems) { company ->
                    CompanyItem(company) {
                        ctx.action.openCompanyActions(company)
                    }
                }
                if (data.items.size < data.count) {
                    item {
                        FooterSection()
                    }
                }
            }
        }
    }
}

@Composable
private fun CompanyItem(company: SharedIO.CompanySimple, onClick: () -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.outlineVariant,
                shape = RoundedCornerShape(4.dp)
            )
            .clip(RoundedCornerShape(4.dp))
            .clickable(onClick = onClick)
            .background(Color.Transparent)
            .padding(horizontal = 16.dp, vertical = 16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = company.name.uppercase(),
                style = MaterialTheme.typography.bodyLarge,
                fontWeight = FontWeight.Bold
            )

            val counter = company.counter
            Text(
                text = "${counter.device} / ${counter.location} / ${counter.user}",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
context(ctx: ListingCompanyCtx)
private fun FooterSection() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 32.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        if (ctx.vm.loadingMore) {
            CircularProgressIndicator(modifier = Modifier.size(24.dp))
        } else {
            OutlinedButton(
                onClick = { ctx.vm.action.loadMore() },
                shape = RoundedCornerShape(4.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = MaterialTheme.colorScheme.primary
                ),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                modifier = Modifier.width(200.dp).height(48.dp)
            ) {
                Text(
                    stringResource(Res.string.ui_company_listing_btn_load_more),
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}

@Composable
context(ctx: ListingCompanyCtx)
private fun CompanyActionsDialog() {
    val company = ctx.vm.activeCompany ?: return

    AlertDialog(
        onDismissRequest = { ctx.action.closeCompanyActions() },
        title = {
            Text(
                text = when (ctx.vm.dialogMode) {
                    DialogMode.Actions -> company.name
                    DialogMode.Rename -> stringResource(Res.string.ui_company_dialog_rename_title)
                    DialogMode.Delete -> stringResource(Res.string.ui_company_dialog_delete_title)
                }
            )
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                if (ctx.vm.dialogLoading) {
                    Box(
                        modifier = Modifier.fillMaxWidth().height(100.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator()
                    }
                } else {
                    ctx.vm.dialogError?.let { err ->
                        Text(
                            text = err,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }

                    when (ctx.vm.dialogMode) {
                        DialogMode.Actions -> {
                            Column(
                                modifier = Modifier.fillMaxWidth(),
                                verticalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                if (ctx.me.permission.company.read) {
                                    Button(
                                        onClick = { ctx.action.executeSwitch() },
                                        modifier = Modifier.fillMaxWidth(),
                                        shape = RoundedCornerShape(4.dp)
                                    ) {
                                        Text(stringResource(Res.string.ui_company_dialog_btn_switch))
                                    }
                                }

                                if (ctx.me.permission.company.write) {
                                    OutlinedButton(
                                        onClick = { ctx.action.setDialogMode(DialogMode.Rename) },
                                        modifier = Modifier.fillMaxWidth(),
                                        shape = RoundedCornerShape(4.dp)
                                    ) {
                                        Text(stringResource(Res.string.ui_company_dialog_btn_rename))
                                    }
                                }

                                if (ctx.me.permission.company.delete && !ctx.me.info.adminMode) {
                                    OutlinedButton(
                                        onClick = { ctx.action.setDialogMode(DialogMode.Delete) },
                                        modifier = Modifier.fillMaxWidth(),
                                        shape = RoundedCornerShape(4.dp),
                                        colors = ButtonDefaults.outlinedButtonColors(
                                            contentColor = MaterialTheme.colorScheme.error
                                        )
                                    ) {
                                        Text(stringResource(Res.string.ui_company_dialog_btn_delete))
                                    }
                                }
                            }
                        }

                        DialogMode.Rename -> {
                            Column(
                                modifier = Modifier.fillMaxWidth(),
                                verticalArrangement = Arrangement.spacedBy(16.dp)
                            ) {
                                OutlinedTextField(
                                    value = ctx.vm.renameName,
                                    onValueChange = { ctx.vm.renameName = it },
                                    modifier = Modifier.fillMaxWidth(),
                                    shape = RoundedCornerShape(4.dp),
                                    placeholder = { Text(stringResource(Res.string.ui_company_dialog_rename_placeholder)) },
                                    singleLine = true
                                )

                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    OutlinedButton(
                                        onClick = { ctx.action.setDialogMode(DialogMode.Actions) },
                                        modifier = Modifier.weight(1f),
                                        shape = RoundedCornerShape(4.dp)
                                    ) {
                                        Text(stringResource(Res.string.ui_company_dialog_btn_cancel))
                                    }

                                    Button(
                                        onClick = { ctx.action.executeRename() },
                                        modifier = Modifier.weight(1f),
                                        shape = RoundedCornerShape(4.dp)
                                    ) {
                                        Text(stringResource(Res.string.ui_company_dialog_btn_confirm))
                                    }
                                }
                            }
                        }

                        DialogMode.Delete -> {
                            Column(
                                modifier = Modifier.fillMaxWidth(),
                                verticalArrangement = Arrangement.spacedBy(16.dp)
                            ) {
                                Text(
                                    text = stringResource(Res.string.ui_company_dialog_delete_warning),
                                    style = MaterialTheme.typography.bodyMedium
                                )

                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    OutlinedButton(
                                        onClick = { ctx.action.setDialogMode(DialogMode.Actions) },
                                        modifier = Modifier.weight(1f),
                                        shape = RoundedCornerShape(4.dp)
                                    ) {
                                        Text(stringResource(Res.string.ui_company_dialog_btn_cancel))
                                    }

                                    Button(
                                        onClick = { ctx.action.executeDelete() },
                                        modifier = Modifier.weight(1f),
                                        shape = RoundedCornerShape(4.dp),
                                        colors = ButtonDefaults.buttonColors(
                                            containerColor = MaterialTheme.colorScheme.error
                                        )
                                    ) {
                                        Text(stringResource(Res.string.ui_company_dialog_btn_confirm))
                                    }
                                }
                            }
                        }
                    }
                }
            }
        },
        confirmButton = {},
        dismissButton = {
            if (ctx.vm.dialogMode == DialogMode.Actions && !ctx.vm.dialogLoading) {
                TextButton(onClick = { ctx.action.closeCompanyActions() }) {
                    Text(stringResource(Res.string.ui_company_dialog_btn_cancel))
                }
            }
        }
    )
}

@Composable
context(ctx: ListingCompanyCtx)
private fun CreateCompanyDialog() {
    if (!ctx.vm.showCreateDialog) return

    AlertDialog(
        onDismissRequest = { ctx.action.closeCreateDialog() },
        title = {
            Text(stringResource(Res.string.ui_company_listing_btn_add_company))
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                if (ctx.vm.dialogLoading) {
                    Box(
                        modifier = Modifier.fillMaxWidth().height(100.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator()
                    }
                } else {
                    ctx.vm.dialogError?.let { err ->
                        Text(
                            text = err,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }

                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        OutlinedTextField(
                            value = ctx.vm.createName,
                            onValueChange = { ctx.vm.createName = it },
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(4.dp),
                            placeholder = { Text("Company name...") },
                            singleLine = true
                        )

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            OutlinedButton(
                                onClick = { ctx.action.closeCreateDialog() },
                                modifier = Modifier.weight(1f),
                                shape = RoundedCornerShape(4.dp)
                            ) {
                                Text(stringResource(Res.string.ui_company_dialog_btn_cancel))
                            }

                            Button(
                                onClick = { ctx.action.executeCreate() },
                                modifier = Modifier.weight(1f),
                                shape = RoundedCornerShape(4.dp)
                            ) {
                                Text(stringResource(Res.string.ui_company_dialog_btn_confirm))
                            }
                        }
                    }
                }
            }
        },
        confirmButton = {}
    )
}
